import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../store";

/**
 * User access levels
 * 0 = Regular user (Home, Support and Feedback, Contact)
 * 1 = Admin (Everything except Admin Panel)
 * 2 = Super Admin (Everything including Admin Panel)
 */
export type UserAccessLevel = 0 | 1 | 2;

export interface UserAccessInfo {
  level: UserAccessLevel;
  isUser: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  canAccessHome: boolean;
  canAccessSupport: boolean;
  canAccessContact: boolean;
  canAccessAnalytics: boolean;
  canAccessPerformance: boolean;
  canAccessTokenConsumption: boolean;
  canAccessUserManagement: boolean;
  canAccessAdminPanel: boolean;
  canAccessProcurementDashboard: boolean;
}

/**
 * Custom hook to get user access information
 * Centralized access control logic based on is_admin field from API
 *
 * IMPORTANT: Only reads from Redux store (API data) - no localStorage fallback
 * This ensures security and single source of truth
 *
 * @returns UserAccessInfo object with access level and permission flags
 */
export const useUserAccess = (): UserAccessInfo => {
  const allAgents = useSelector((state: RootState) => state.agents.data);

  const accessLevel = useMemo((): UserAccessLevel => {
    // Get from Redux store (API data) only
    if (allAgents.length > 0) {
      const is_admin = allAgents[0]?.is_admin;
      if (is_admin === 2) return 2; // Super Admin
      if (is_admin === 1) return 1; // Admin
      return 0; // User (or undefined is_admin)
    }

    // Default to user level if no data loaded yet
    return 0;
  }, [allAgents]);

  const accessInfo: UserAccessInfo = useMemo(() => {
    const isUser = accessLevel === 0;
    const isAdmin = accessLevel === 1;
    const isSuperAdmin = accessLevel === 2;

    return {
      level: accessLevel,
      isUser,
      isAdmin,
      isSuperAdmin,
      // Regular user permissions
      canAccessHome: true, // All users can access home
      canAccessSupport: true, // All users can access support/feedback
      canAccessContact: true, // All users can access contact
      // Admin permissions (level 1 and above)
      canAccessAnalytics: isAdmin || isSuperAdmin,
      canAccessPerformance: isAdmin || isSuperAdmin,
      canAccessTokenConsumption: isAdmin || isSuperAdmin,
      canAccessProcurementDashboard: isAdmin || isSuperAdmin,
      // Super admin only permissions (level 2)
      canAccessUserManagement: isSuperAdmin,
      canAccessAdminPanel: isSuperAdmin,
    };
  }, [accessLevel]);

  return accessInfo;
};
